import { useState, useEffect, useRef, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Send, Clock, CheckCircle, AlertCircle, Inbox, Trash2, HelpCircle } from "lucide-react";
import { format } from "date-fns";

interface SupportTicket {
  id: string;
  subject: string;
  message: string;
  status: string;
  admin_reply: string | null;
  created_at: string;
}

export const AffiliateSupportTab = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const isRTL = language === 'ar';

  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const hasFetched = useRef(false);

  const fetchTickets = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('affiliate_support_requests' as any)
      .select('id, subject, message, status, admin_reply, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    setTickets((data as unknown as SupportTicket[]) || []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchTickets();
  }, [fetchTickets]);

  const handleDelete = async (ticketId: string) => {
    setDeletingId(ticketId);
    const { error } = await supabase
      .from('affiliate_support_requests' as any)
      .delete()
      .eq('id', ticketId);
    if (error) {
      toast({ title: isRTL ? 'خطأ' : 'Error', description: error.message, variant: 'destructive' });
    } else {
      setTickets(prev => prev.filter(t => t.id !== ticketId));
      toast({ title: isRTL ? 'تم الحذف' : 'Deleted', description: isRTL ? 'تم حذف طلب الدعم' : 'Support ticket deleted' });
    }
    setDeletingId(null);
  };

  const handleSubmit = async () => {
    if (!subject.trim() || !message.trim() || !user) return;
    setSubmitting(true);

    const { error } = await supabase.from('affiliate_support_requests' as any).insert({
      user_id: user.id,
      subject: subject.trim(),
      message: message.trim(),
      status: 'open',
    });

    if (error) {
      toast({ title: isRTL ? 'خطأ' : 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: isRTL ? 'تم الإرسال' : 'Submitted', description: isRTL ? 'تم إرسال طلب الدعم. سيتواصل معك الفريق قريباً.' : 'Support request submitted. Our team will get back to you shortly.' });
      setSubject(""); setMessage(""); fetchTickets();
    }
    setSubmitting(false);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open': return <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-0 gap-1 text-[11px]"><Clock className="h-3 w-3" />{isRTL ? 'مفتوح' : 'Open'}</Badge>;
      case 'resolved': return <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 border-0 gap-1 text-[11px]"><CheckCircle className="h-3 w-3" />{isRTL ? 'محلول' : 'Resolved'}</Badge>;
      case 'in_progress': return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-0 gap-1 text-[11px]"><AlertCircle className="h-3 w-3" />{isRTL ? 'قيد المعالجة' : 'In Progress'}</Badge>;
      default: return <Badge variant="outline" className="text-[11px]">{status}</Badge>;
    }
  };

  const faqs = [
    {
      q: isRTL ? "كيف أحسب عمولتي؟" : "How is my commission calculated?",
      a: isRTL ? "يتم حساب عمولتك كنسبة مئوية من إجمالي قيمة الحملة التي تم حجزها عبر رابط الإحالة الخاص بك. يتم تحديد النسبة عند الموافقة على طلبك."
        : "Your commission is calculated as a percentage of the total campaign value booked through your referral link. The rate is set when your application is approved.",
    },
    {
      q: isRTL ? "متى أحصل على عمولتي؟" : "When do I get paid?",
      a: isRTL ? "يتم دفع العمولات بعد تأكيد الحملة واكتمال الدفع من العميل. تتم عمليات الدفع شهرياً."
        : "Commissions are paid after the campaign is confirmed and the client payment is completed. Payouts are processed monthly.",
    },
    {
      q: isRTL ? "هل يمكنني إنشاء حملات نيابة عن عملائي؟" : "Can I create campaigns on behalf of my clients?",
      a: isRTL ? "نعم! يمكنك استخدام تبويب 'إنشاء حملة' لإنشاء حملات نيابة عن عملائك. سيتم ربط الحملة بحسابك تلقائياً."
        : "Yes! Use the 'Create Campaign' tab to create campaigns on behalf of your clients. The campaign will be automatically linked to your account.",
    },
    {
      q: isRTL ? "كيف أضيف عملاء جدد؟" : "How do I add new clients?",
      a: isRTL ? "يمكنك إضافة العملاء يدوياً من تبويب 'العملاء'، أو سيتم إضافتهم تلقائياً عند إنشاء حملات نيابة عنهم."
        : "You can add clients manually from the 'Clients' tab, or they'll be added automatically when you create campaigns on their behalf.",
    },
    {
      q: isRTL ? "هل يمكنني تعديل أسعار الحملات؟" : "Can I modify campaign pricing?",
      a: isRTL ? "لا. كشريك إحالة، يمكنك فقط عرض الأسعار المحددة من المنصة. لا يمكنك إنشاء أسعار مخصصة أو إصدار عقود مستقلة."
        : "No. As a referral partner, you can only view platform-set pricing. You cannot create custom pricing or issue independent contracts.",
    },
    {
      q: isRTL ? "كيف أتتبع حالة حملاتي؟" : "How do I track my campaign status?",
      a: isRTL ? "استخدم تبويب 'حجوزاتي' لمتابعة جميع حملاتك. يمكنك تصفية الحملات حسب الحالة وعرض تفاصيل كل حملة."
        : "Use the 'My Bookings' tab to monitor all your campaigns. You can filter by status and expand each campaign to see details.",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Support Request Form */}
      <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
        <div style={{ position: 'relative' }}>
          <div className="h-0.5 bg-gradient-to-r from-primary to-primary/60" />
        </div>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border/50 bg-muted/20">
          <div className="p-2 rounded-xl bg-primary/10">
            <MessageSquare className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground">{isRTL ? 'إرسال طلب دعم' : 'Submit Support Request'}</h2>
            <p className="text-xs text-muted-foreground">{isRTL ? 'سيرد عليك فريق الدعم في أقرب وقت' : 'Our team will respond as soon as possible'}</p>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">{isRTL ? 'الموضوع' : 'Subject'}</Label>
            <Input
              className="rounded-xl h-11"
              placeholder={isRTL ? 'مثال: مشكلة في العمولات' : 'e.g. Issue with commissions'}
              value={subject}
              onChange={e => setSubject(e.target.value)}
              maxLength={200}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">{isRTL ? 'الرسالة' : 'Message'}</Label>
            <Textarea
              className="rounded-xl resize-none"
              placeholder={isRTL ? 'اشرح مشكلتك أو استفسارك بالتفصيل...' : 'Describe your issue or question in detail...'}
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={4}
              maxLength={2000}
            />
            <p className="text-xs text-muted-foreground text-right">{message.length}/2000</p>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={!subject.trim() || !message.trim() || submitting}
            className="rounded-xl gap-2"
          >
            {submitting ? <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-primary-foreground" /> : <Send className="h-4 w-4" />}
            {isRTL ? 'إرسال الطلب' : 'Submit Request'}
          </Button>
        </div>
      </div>

      {/* Previous Tickets */}
      {!loading && (
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border/50 bg-muted/20">
            <div className="p-2 rounded-xl bg-muted">
              <Clock className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">{isRTL ? 'الطلبات السابقة' : 'Previous Requests'}</h3>
              <p className="text-xs text-muted-foreground">{tickets.length} {isRTL ? 'طلب' : 'tickets'}</p>
            </div>
          </div>
          <div className="divide-y divide-border/40">
            {tickets.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="p-4 rounded-2xl bg-muted/50 mb-3">
                  <Inbox className="h-8 w-8 text-muted-foreground opacity-40" />
                </div>
                <p className="text-sm text-muted-foreground">{isRTL ? 'لا توجد طلبات سابقة' : 'No previous requests'}</p>
              </div>
            ) : (
              tickets.map(ticket => (
                <div key={ticket.id} className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="font-semibold text-sm text-foreground">{ticket.subject}</p>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {getStatusBadge(ticket.status)}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDelete(ticket.id)}
                        disabled={deletingId === ticket.id}
                      >
                        {deletingId === ticket.id
                          ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-t-2 border-current" />
                          : <Trash2 className="h-3.5 w-3.5" />
                        }
                      </Button>
                    </div>
                  </div>

                  <div className="bg-muted/50 rounded-xl rounded-tl-sm p-3 text-xs text-muted-foreground mb-2">
                    {ticket.message}
                  </div>

                  {ticket.admin_reply && (
                    <div className="bg-primary/5 border border-primary/15 rounded-xl rounded-tr-sm p-3 text-xs ml-4">
                      <p className="text-xs font-semibold text-primary mb-1">{isRTL ? 'رد الإدارة' : 'Admin Reply'}</p>
                      <p className="text-foreground">{ticket.admin_reply}</p>
                    </div>
                  )}

                  <p className="text-[11px] text-muted-foreground mt-2">
                    {format(new Date(ticket.created_at), 'MMM d, yyyy · h:mm a')}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* FAQ */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            {isRTL ? "الأسئلة الشائعة" : "Frequently Asked Questions"}
          </CardTitle>
          <CardDescription>
            {isRTL ? "إجابات للأسئلة الأكثر شيوعاً حول برنامج الشراكة" : "Answers to common questions about the partner program"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-sm text-left">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
};
